import { FC } from "react";
import { Box, Typography } from "@material-ui/core";
import { FormikBag, FormikProps, withFormik } from "formik";
import * as yup from "yup";

import {
  AllAccessDiscountTooltip,
  Button,
  CheckboxField,
  LabelField,
  NumberField,
  ROIEquation,
  UsdIntegerInput,
} from "components";
import { getReturnOnInvestment } from "lib/finance";
import { formatCurrencyAmount, formatPercentage } from "lib/format";
import { parseIntegerAmount, parseNumber } from "lib/parse";
import { ListingKind, ResaleInfo } from "services/listings/types";
import { ListingOffer } from "services/offers/types";
import theme from "theme";

export interface FormValues {
  amount: string;
  updateListPrice: boolean;
}

interface FormProps {
  buyNowPrice: number;
  highestOpenBuyerOfferAmount: number;
  humanizedOfferExpirationDate: string;
  initialValues: FormValues;
  isAcceptingFinalOffersEnabled: boolean;
  isOwner: boolean;
  ltm: number;
  listingKind: ListingKind;
  minimumPrice: number;
  offer: ListingOffer;
  offers: ListingOffer[];
  onSave: (values: FormValues) => Promise<void>;
  onSaveSuccess: () => void;
  resaleInfo: ResaleInfo;
  royxTake: number;
}

const MakeCounterofferForm: FC<FormProps & FormikProps<FormValues>> = ({
  values,
  errors,
  touched,
  handleChange,
  handleBlur,
  handleSubmit,
  isSubmitting,
  buyNowPrice,
  humanizedOfferExpirationDate,
  isAcceptingFinalOffersEnabled,
  isOwner,
  listingKind,
  ltm,
  resaleInfo,
  royxTake,
}) => {
  const offerAmount = parseNumber(values.amount, 0);
  const royxTakeAmount = offerAmount * royxTake;
  const saleProceeds = offerAmount * (1 - royxTake);

  const trailingYield = () => {
    const offerAmount = parseNumber(values.amount, 0);
    return offerAmount ? ((ltm / offerAmount) * 100).toFixed(2) : 0;
  };

  const returnOnInvestment: number =
    !!saleProceeds && !!resaleInfo
      ? getReturnOnInvestment(
          resaleInfo.royalties_received,
          saleProceeds,
          resaleInfo.purchased_amount,
          resaleInfo.buyer_fee,
        )
      : null;
  return (
    <form onSubmit={handleSubmit}>
      {isOwner ? (
        <>
          <Typography component="div" gutterBottom>
            <Box fontSize={13}>
              The buyer who made this offer will have{" "}
              {humanizedOfferExpirationDate} to accept, reject, or counter.
            </Box>
          </Typography>
          {isAcceptingFinalOffersEnabled && (
            <Typography component="div" gutterBottom>
              <Box fontSize={13}>
                If the buyer accepts your counteroffer, a 24-hour auction will
                begin starting at the accepted amount and other buyers will have
                a final chance to bid on your asset. If no other bids are made
                during the auction, you agree to sell this asset for this
                amount.
              </Box>
            </Typography>
          )}
          <Typography component="div" gutterBottom>
            <Box fontSize={13}>
              If your counteroffer is rejected, you will not be able to make
              another one.
            </Box>
          </Typography>
        </>
      ) : (
        <>
          <Typography component="div" gutterBottom>
            <Box fontSize={13}>
              You cannot edit or withdraw your counteroffer.
            </Box>
          </Typography>
          {isAcceptingFinalOffersEnabled && (
            <Typography component="div" gutterBottom>
              <Box fontSize={13}>
                If the seller accepts your counteroffer, a 24-hour auction will
                begin starting at the accepted amount. If no other investors
                bid, you will win this asset at the accepted price. You will
                have the opportunity to bid again during the final auction.
              </Box>
            </Typography>
          )}
          <Typography component="div" gutterBottom>
            <Box fontSize={13}>
              If your counteroffer is rejected and you have a proxy in place,
              your proxy will place an offer one increment higher than your last
              offer {humanizedOfferExpirationDate} after the offer that started
              the counteroffer chain.
            </Box>
          </Typography>
        </>
      )}
      <Box my={2} textAlign="center">
        <Box my={2}>
          <NumberField
            name="amount"
            type="text"
            fullWidth
            label="Counteroffer"
            value={values.amount ? parseIntegerAmount(values.amount) : ""}
            helperText={
              touched.amount && !!errors.amount
                ? errors.amount
                : isOwner
                ? ""
                : `Trailing Earning Yield: ${trailingYield()}%`
            }
            disabled={isSubmitting}
            onBlur={handleBlur}
            onChange={handleChange}
            error={touched.amount && !!errors.amount}
            inputComponent={UsdIntegerInput}
          />
        </Box>
        {isOwner && (
          <>
            <Box my={2}>
              <LabelField
                label={`\u2212 Marketplace Fee (${formatPercentage(royxTake)})`}
                value={
                  isAcceptingFinalOffersEnabled
                    ? `\u2212 ${formatPercentage(royxTake)}`
                    : `\u2212${formatCurrencyAmount("USD", royxTakeAmount)}`
                }
                tooltipText={
                  listingKind !== "direct_listing" ? (
                    <AllAccessDiscountTooltip />
                  ) : undefined
                }
                interactiveTooltip
              />
            </Box>
            <Box my={2}>
              <LabelField
                label={`= ${
                  isAcceptingFinalOffersEnabled ? "Minimum" : "Your"
                } Sale Proceeds`}
                value={`${formatCurrencyAmount("USD", saleProceeds)}`}
              />
            </Box>
            {!!resaleInfo && !!resaleInfo?.last_update && (
              <Box my={2}>
                <LabelField
                  label={`${
                    isAcceptingFinalOffersEnabled ? "Minimum " : ""
                  }Return On Investment`}
                  tooltipText={<ROIEquation />}
                  value={formatPercentage(returnOnInvestment)}
                />
              </Box>
            )}
            <Box my={2}>
              <CheckboxField
                name="updateListPrice"
                checked={values.updateListPrice}
                disabled={
                  !!buyNowPrice && buyNowPrice === parseNumber(values.amount, 0)
                }
                label="Update your list price and make this price available to everyone?"
                checkboxColor={theme.custom.palette.blue.main}
                onBlur={handleBlur}
                onChange={handleChange}
              />
            </Box>
          </>
        )}
        <Box my={2}>
          <Button
            customColor="blue"
            type="submit"
            fullWidth
            size="large"
            variant="contained"
            disabled={isSubmitting}
            id="send-offer"
          >
            Send Counteroffer
          </Button>
        </Box>
      </Box>
    </form>
  );
};

const formikOptions = {
  mapPropsToValues: (props: FormProps) => props.initialValues,

  validationSchema: (props: FormProps) => {
    const {
      buyNowPrice,
      highestOpenBuyerOfferAmount,
      isOwner,
      ltm,
      offer,
      offers,
      minimumPrice,
    } = props;
    const counteredOfferAmount = Number(
      offers.find(otherOffer => otherOffer.id === offer.countered_offer_id)
        ?.amount,
    );

    return yup.object().shape({
      amount: yup
        .string()
        .test(
          "maxDigits",
          "Ensure that there are no more than 16 digits in total.",
          amount => {
            return !amount || amount.replace(/[^0-9]/g, "").length <= 16;
          },
        )
        .test(
          "maxDigits",
          "Ensure that there are no more than 13 digits before the decimal point.",
          amount => {
            return (
              !amount ||
              amount.replace(/[^0-9.]/g, "").split(".")[0].length <= 13
            );
          },
        )
        .test("maxLtm", "LTM multiple must be less than 99", amount => {
          return !amount || parseNumber(amount, 0) / ltm < 99;
        })
        .test(
          "max",
          `The counteroffer amount must be lower than ${
            isOwner
              ? !!counteredOfferAmount && !buyNowPrice
                ? "your last counteroffer"
                : !!buyNowPrice && counteredOfferAmount <= buyNowPrice
                ? "your last counteroffer"
                : "or equal to the list price"
              : "the current counteroffer"
          }`,
          amount => {
            const amountNumber = parseNumber(amount, 0);
            const isSellerCounterofferValid =
              !!buyNowPrice && !!counteredOfferAmount
                ? amountNumber <= buyNowPrice &&
                  amountNumber < counteredOfferAmount
                : !!buyNowPrice
                ? amountNumber <= buyNowPrice
                : !!counteredOfferAmount
                ? amountNumber < counteredOfferAmount
                : true;
            const isBuyerCounterofferValid =
              amountNumber < parseNumber(props.offer.amount, 0);
            return (
              !amount ||
              (isOwner ? isSellerCounterofferValid : isBuyerCounterofferValid)
            );
          },
        )
        .test(
          "greaterThanPreviousOffer",
          `The counteroffer amount must be greater than ${
            isOwner ? "the current offer" : "your last offer"
          }`,
          amount =>
            !amount ||
            parseNumber(amount, 0) >
              (isOwner ? Number(offer.amount) : counteredOfferAmount),
        )
        .test(
          "greaterThanHighestOpenBuyerOfferAmount",
          `The counteroffer amount must be greater than the highest open offer of ${formatCurrencyAmount(
            "USD",
            highestOpenBuyerOfferAmount,
          )}`,
          amount =>
            isOwner || parseNumber(amount, 0) > highestOpenBuyerOfferAmount,
        )
        .test(
          "smallerThanListingMinimumPrice",
          `The counteroffer amount must be greater than listing minimum bid of ${formatCurrencyAmount(
            "USD",
            minimumPrice,
          )}`,
          amount => !amount || parseNumber(amount, 0) > minimumPrice,
        )
        .required("Required"),
      updateListPrice: yup.boolean(),
    });
  },

  handleSubmit: async (
    values: FormValues,
    { props, setSubmitting }: FormikBag<FormProps, FormValues>,
  ) => {
    try {
      await props.onSave(values);
    } catch {
      // pass
    } finally {
      setSubmitting(false);
      props.onSaveSuccess();
    }
  },
};

const EnhancedMakeCounterofferForm = withFormik<FormProps, FormValues>(
  formikOptions,
)(MakeCounterofferForm);

export default EnhancedMakeCounterofferForm;
